// Central industry configuration — the single source of truth for FinlyBooks editions.
// The user's chosen industry is stored on the company profile as `industry`.
// Every screen reads from here so behaviour stays consistent and easy to change.

export const INDUSTRIES = {
  tourism: {
    id: 'tourism',
    name: 'Tourism & Travel',
    tagline: 'For travel agencies, tour operators & ticketing',
    icon: '✈️',
    accent: '#0f6e56',
    // What the customer/supplier are called
    customerLabel: 'Customer',
    supplierLabel: 'Supplier / Airline',
    // Invoice heading + the "line" being billed
    invoiceNoun: 'Booking',
    baseAmountLabel: 'Fare / Base Amount',
    // Which extra detail fields the invoice form shows (in order)
    fields: [
      { key: 'pnr', label: 'Booking / PNR Ref', placeholder: 'e.g. AB12CD' },
      { key: 'paxName', label: 'Passenger Name(s)', placeholder: 'e.g. Mr. A. Khan' },
      { key: 'sector', label: 'Sector / Route', placeholder: 'e.g. DOH-DXB-DOH' },
      { key: 'airline', label: 'Airline / Supplier', placeholder: 'e.g. Qatar Airways' },
      { key: 'serviceDate', label: 'Travel Date', type: 'date' },
      { key: 'returnDate', label: 'Return Date', type: 'date' },
      { key: 'refNo', label: 'Ticket / Doc No.', placeholder: 'e.g. 157-1234567890' },
      { key: 'description', label: 'Service Description', placeholder: 'e.g. Air ticket + visa' },
    ],
    detailsTitle: '✈ Travel Details',
    personLabel: 'Passenger',
    multiCurrency: true,
  },

  service: {
    id: 'service',
    name: 'Service Business',
    tagline: 'For agencies, consultants, salons, repair & professional services',
    icon: '🛠️',
    accent: '#185fa5',
    customerLabel: 'Client',
    supplierLabel: 'Vendor / Subcontractor',
    invoiceNoun: 'Job',
    baseAmountLabel: 'Service Amount',
    fields: [
      { key: 'jobName', label: 'Job / Project Name', placeholder: 'e.g. Website redesign' },
      { key: 'staffName', label: 'Staff / Consultant', placeholder: 'e.g. Sara' },
      { key: 'serviceDate', label: 'Service Date', type: 'date' },
      { key: 'hours', label: 'Hours / Units', placeholder: 'e.g. 12', type: 'number' },
      { key: 'rate', label: 'Rate per Hour / Unit', placeholder: 'e.g. 50', type: 'number' },
      { key: 'description', label: 'Work Description', placeholder: 'e.g. UI design & build' },
    ],
    detailsTitle: '🛠 Service Details',
    personLabel: 'Client',
    // when both hours & rate are present, fare auto-fills hours*rate
    autoFareFrom: ['hours', 'rate'],
  },

  general: {
    id: 'general',
    name: 'General Accounting',
    tagline: 'Day-to-day bookkeeping for shops, traders & small business',
    icon: '📒',
    accent: '#7a4fb5',
    customerLabel: 'Customer',
    supplierLabel: 'Supplier',
    invoiceNoun: 'Invoice',
    baseAmountLabel: 'Amount',
    // General supports an optional simple item line; full item-list can be added later
    fields: [
      { key: 'itemName', label: 'Item / Product', placeholder: 'e.g. Rice bag 25kg' },
      { key: 'qty', label: 'Quantity', placeholder: 'e.g. 10', type: 'number' },
      { key: 'rate', label: 'Rate', placeholder: 'e.g. 40', type: 'number' },
      { key: 'description', label: 'Description / Notes', placeholder: 'optional' },
    ],
    detailsTitle: '📦 Item Details',
    personLabel: 'Customer',
    autoFareFrom: ['qty', 'rate'],
    simpleModeAllowed: true, // user can skip items and just enter an amount
  },
};

export const INDUSTRY_LIST = Object.values(INDUSTRIES);
export const DEFAULT_INDUSTRY = 'general';

// Safe getter — always returns a valid config
export function getIndustry(id) {
  return INDUSTRIES[id] || INDUSTRIES[DEFAULT_INDUSTRY];
}

// Given an industry config + form values, compute the base fare when auto-derivable
export function deriveFare(cfg, form) {
  if (cfg.autoFareFrom) {
    const [a, b] = cfg.autoFareFrom;
    const av = parseFloat(form[a]);
    const bv = parseFloat(form[b]);
    if (av > 0 && bv > 0) return av * bv;
  }
  return null; // caller keeps manual fareAmount
}


// Common currencies for the multi-currency picker (base currency set in Company Settings)
export const COMMON_CURRENCIES = ['QAR', 'USD', 'EUR', 'GBP', 'INR', 'AED', 'SAR', 'KWD', 'BHD', 'OMR', 'PHP', 'THB', 'RWF', 'JMD', 'CAD', 'AUD'];
